import graphql__95c7992a42018b8efd4c from "./__generated__/AuthenticationSignUp_authenticationConfig.graphql.js";import { useCallback, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import withFormStore from "../Form/withFormStore";
import { useAlert } from "../Alerts/withAlert";
import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS } from "../Alerts/Alert";
import StringField from "../Form/Field/StringField";
import EmailField from "../Form/Field/EmailField";
import PasswordField from "../Form/Field/PasswordField";
import CheckBoxField from "../Form/Field/CheckBoxField";
import EmailTrackingConsentField from "../Form/Field/EmailTrackingConsentField";
import Answers from "../Answer/Answers";
import Button from "../Buttons/Button";
import { intlMessages as formPageIntlMessages } from "../ParticipativeModule/FormPage";
import { useFormatMessage } from "../Intl/Context";
import AuthenticationTwoFactorManager from "./AuthenticationTwoFactorManager";

import { signUp } from "../../mutations/SignUpMutation";

const AuthenticationSignUp = ({ withoutPadding = false, ...props }) => {
  const authenticationConfig = useFragment(
    AuthenticationSignUp_authenticationConfig,
    props.authenticationConfig
  );

  const {
    privacyPolicyPageUrl,
    emailConfirmationRequired,
    enableEmailConsent,
    emailConsentLabel,
    userSubscriptionFormResponse
  } = authenticationConfig;

  const { answers } = userSubscriptionFormResponse || { answers: null };

  const { onSignIn, pushErrors, validateForm, formState } = props;

  const t = useFormatMessage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState(null);

  const successMessageKey = emailConfirmationRequired
    ? intlMessages.success
    : intlMessages.successWithoutEmailConfirmation;

  const title = t(intlMessages.title, {
    b: message => `<b>${message}</b>`,
    br: "<br>"
  });
  const requiredFieldsHint = t(formPageIntlMessages.requiredFieldsHint);
  const name = t(intlMessages.name);
  const email = t(intlMessages.email);
  const password = t(intlMessages.password);
  const passwordConfirmation = t(intlMessages.passwordConfirmation);
  const gdprMention = t(intlMessages.gdprMention, {
    a: message => `<a href="${privacyPolicyPageUrl}" target="_blank">${message}</a>`
  });
  const submit = t(intlMessages.submit);
  const success = t(successMessageKey);

  const formClassName = classNames(
    "authentication-fieldset",
    "authentication-sign-up",
    "box",
    "box-margin-top-mobile",
    "margin-bottom-xs",
    {
      "padding-md": !withoutPadding,
      loading: isSubmitting
    }
  );

  const { showAlert } = useAlert();

  const handleSignedIn = useCallback(() => {
    showAlert({
      message: success,
      type: ALERT_TYPE_SUCCESS
    });

    if (onSignIn) {
      onSignIn();
    } else {
      // Reload page on sign in to avoid stale data to hang around after
      // the user is authenticated.
      window.location.href = window.location;
    }
  }, [onSignIn, showAlert, success]);

  const handleSignUpResponse = useCallback(
    ({ signUp: response }) => {
      setIsSubmitting(false);

      if (response.success) {
        handleSignedIn();
      } else if (response.twoFactorStep) {
        // Credentials were valid but the user must complete an SMS step before being
        // signed in; swap the sign-in form for the matching two-factor step.
        setTwoFactorStep(response.twoFactorStep);
      } else {
        showAlert({
          message: response.notice,
          type: ALERT_TYPE_ERROR
        });

        pushErrors(response.errors);
      }
    },
    [handleSignedIn, pushErrors, showAlert]
  );

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      if (validateForm()) {
        setIsSubmitting(true);

        signUp(formState, handleSignUpResponse);
      }
    },
    [formState, handleSignUpResponse, validateForm]
  );

  if (twoFactorStep) {
    return (
      <AuthenticationTwoFactorManager
        onSignIn={handleSignedIn}
        twoFactorStep={twoFactorStep}
      />
    );
  }

  return (
    <form className={formClassName} onSubmit={handleSubmit} data-sign-up-form>
      <fieldset>
        <legend
          className="text-subtitle-page"
          dangerouslySetInnerHTML={{ __html: title }}
        />

        <p
          className="form-hint-required-fields margin-bottom-sm text-small"
          dangerouslySetInnerHTML={{ __html: requiredFieldsHint }}
        />
        <StringField fieldName="name" required label={name} autoComplete="nickname" />

        <EmailField fieldName="email" required label={email} autoComplete="email" />

        <PasswordField
          fieldName="password"
          required
          label={password}
          autoComplete="new-password"
        />

        <PasswordField
          fieldName="passwordConfirmation"
          required
          label={passwordConfirmation}
          confirmationOf="password"
          autoComplete="new-password"
        />

        <Answers answers={answers} />

        <div className="checkboxes-no-background">
          {enableEmailConsent && (
            <CheckBoxField fieldName="emailConsent" label={emailConsentLabel} />
          )}

          <EmailTrackingConsentField />
        </div>

        {privacyPolicyPageUrl && (
          <p
            className="text-muted text-smaller margin-bottom-sm margin-bottom-sm"
            dangerouslySetInnerHTML={{ __html: gdprMention }}
          />
        )}
      </fieldset>

      <Button
        type="submit"
        className="btn btn-primary btn-block-md"
        onClick={handleSubmit}
        label={submit}
        disabled={isSubmitting}
        data-loading={isSubmitting}
        iconClassName="icon-arrow-right"
        iconPosition="after"
        data-sign-up-submit-button
      />
    </form>
  );
};

const AuthenticationSignUp_authenticationConfig = graphql__95c7992a42018b8efd4c;

export default withFormStore(AuthenticationSignUp);

export const intlMessages = defineMessages({
  title: {
    id: "authentication.signUp.title",
    defaultMessage: "Pas encore de compte ?{br}<b>Créez-le en une minute !</b>"
  },
  name: {
    id: "authentication.signUp.labels.name",
    defaultMessage: "Nom d'utilisateur / Pseudo"
  },
  email: {
    id: "authentication.signUp.labels.email",
    defaultMessage: "E-mail"
  },
  password: {
    id: "authentication.signUp.labels.password",
    defaultMessage: "Mot de passe"
  },
  passwordConfirmation: {
    id: "authentication.signUp.labels.passwordConfirmation",
    defaultMessage: "Confirmation du mot de passe"
  },
  // Note :
  //   The route is translated directly from the translations file, therefore it's
  //   not needed to try translating it with pathFor.
  //
  gdprMention: {
    id: "authentication.signUp.labels.gdprMention",
    defaultMessage: `
      En application du Règlement Général sur la Protection des Données (RGPD)
      vous disposez d’un droit d’accès, de rectification ou de suppression que
      vous pouvez exercer directement depuis votre espace personnel sur la
      plateforme ou en envoyant un e-mail au gestionnaire de la plateforme à
      l’adresse rgpd@respublica-conseil.fr. Pour plus d’informations, vous
      pouvez consulter notre <a>Politique de confidentialité</a>
    `
  },
  submit: {
    id: "authentication.signUp.submit",
    defaultMessage: "Créer mon compte"
  },
  success: {
    id: "authentication.signUp.success",
    defaultMessage: `
      Votre compte a bien été créé et vous êtes maintenant connectés. Un e-mail
      de confirmation vous a été envoyé, vous devez confirmer votre adresse
      e-mail pour pouvoir continuer à utiliser votre compte après 24h.
    `
  },
  successWithoutEmailConfirmation: {
    id: "authentication.signUp.successWithoutEmailConfirmation",
    defaultMessage: "Votre compte a bien été créé et vous êtes maintenant connectés."
  }
});
